import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, Download, Mail, Heart, Calendar, DollarSign, User } from 'lucide-react';
import Nav from '../components/Nav';

const ReceiptPage = () => {
  const location = useLocation();
  const { donation, type } = location.state || {};

  if (!donation) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">No Receipt Found</h1>
          <Link to="/" className="glow-button">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation (hidden in print) */}
      <div className="print:hidden">
        <Nav />
      </div>

      {/* Receipt Content */}
      <section className="pt-32 pb-12 px-6 print:pt-12">
        <div className="max-w-3xl mx-auto">
          {/* Success Animation */}
          <div className="text-center mb-8 animate-fade-in print:hidden">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={60} className="text-green-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Thank You!</h1>
            <p className="text-xl text-gray-300">
              Your support means the world 💜
            </p>
          </div>

          {/* Receipt Card */}
          <div className="glass-card p-8 md:p-12 space-y-6" data-testid="receipt-card">
            {/* Header */}
            <div className="text-center border-b border-white/10 pb-6">
              <h2 className="text-3xl font-bold gradient-text mb-2">DIONLOVELLE</h2>
              <p className="text-gray-400">Artist • Vocalist • Songwriter</p>
              <p className="text-sm text-gray-500 mt-2">dionlovellebooking@gmail.com</p>
            </div>

            {/* Receipt Type */}
            <div className="flex items-center justify-center gap-2 py-4">
              <Heart size={24} className="text-purple-400" />
              <h3 className="text-2xl font-bold">Donation Receipt</h3>
            </div>

            {/* Receipt Details */}
            <div className="space-y-4 bg-white/5 rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 text-gray-400">
                  <User size={18} />
                  <span>Donor Name:</span>
                </div>
                <span className="font-semibold text-right">{donation.name}</span>
              </div>

              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail size={18} />
                  <span>Email:</span>
                </div>
                <span className="font-semibold text-right break-all">{donation.email}</span>
              </div>

              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar size={18} />
                  <span>Date:</span>
                </div>
                <span className="font-semibold">
                  {new Date(donation.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>

              <div className="flex justify-between items-start">
                <span className="text-gray-400">Receipt ID:</span>
                <span className="font-mono text-sm text-right">{donation.id}</span>
              </div>

              {donation.message && donation.message !== 'No message provided' && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-gray-400 mb-2">Message:</p>
                  <p className="italic text-gray-300">{donation.message}</p>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="bg-gradient-to-r from-purple-500/20 to-gold-500/20 border border-purple-500/30 rounded-lg p-8 text-center">
              <p className="text-gray-400 mb-2">Donation Amount</p>
              <div className="flex items-center justify-center gap-2">
                <DollarSign size={40} className="text-purple-400" />
                <span className="text-6xl font-bold gradient-text">{parseFloat(donation.amount).toFixed(2)}</span>
              </div>
            </div>

            {/* Thank You Message */}
            <div className="bg-white/5 rounded-lg p-6 text-center">
              <p className="text-lg text-gray-300 mb-4">
                Your generous contribution will help fund:
              </p>
              <ul className="space-y-2 text-gray-400">
                <li>✨ Future music projects and recordings</li>
                <li>🎵 Professional production and mixing</li>
                <li>🎸 New equipment and instruments</li>
                <li>📈 Overall artist development and growth</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 print:hidden">
              <button
                onClick={handlePrint}
                className="flex-1 glow-button flex items-center justify-center gap-2"
                data-testid="print-receipt-button"
              >
                <Download size={20} />
                Print/Save Receipt
              </button>
              <Link
                to="/"
                className="flex-1 glass-card px-6 py-3 text-center hover:bg-purple-500/20 transition-all font-semibold"
                data-testid="return-home-button"
              >
                Return Home
              </Link>
            </div>

            {/* Email Confirmation */}
            <div className="text-center text-sm text-gray-400 border-t border-white/10 pt-6">
              <p>A copy of this receipt has been sent to <strong className="text-gray-300">{donation.email}</strong></p>
              <p className="mt-2">Receipt confirmation also sent to <strong className="text-gray-300">dionlovellebooking@gmail.com</strong></p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="text-center mt-8 text-sm text-gray-500 print:hidden">
            <p>This is your official donation receipt for your records.</p>
            <p className="mt-2">Questions? Contact dionlovellebooking@gmail.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Dionlovelle. All rights reserved.</p>
        </div>
      </footer>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
            color: black;
          }
          .glass-card {
            background: white;
            border: 1px solid #ddd;
          }
          .gradient-text {
            color: black !important;
            -webkit-text-fill-color: black !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:pt-12 {
            padding-top: 3rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReceiptPage;
